import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { TableRow, TableCell } from '@/components/ui/table';

interface TableSkeletonRowsProps {
  columns: number;
  rows?: number;
}

export function TableSkeletonRows({ columns, rows = 8 }: TableSkeletonRowsProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i} className="border-b border-gray-100 dark:border-gray-800/80 hover:bg-transparent">
          {Array.from({ length: columns }).map((_, j) => (
            <TableCell key={j} className="px-4 py-3.5 text-center">
              <Skeleton
                className={`h-4.5 rounded-lg mx-auto ${
                  j === 0 ? 'w-6' : j === 1 ? 'w-16' : j === 2 ? 'w-8' : j === 7 ? 'w-20' : 'w-24'
                }`}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export function InboundMobileCardSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="p-3.5 sm:p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-sm space-y-3"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <Skeleton className="w-8 h-8 rounded-full shrink-0" />
              <div className="space-y-1.5 min-w-0">
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-3 w-36 rounded-md" />
              </div>
            </div>
            <Skeleton className="h-5 w-16 rounded-full shrink-0" />
          </div>

          {/* Amount Box */}
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div className="space-y-1">
              <Skeleton className="h-2.5 w-12 rounded" />
              <Skeleton className="h-5 w-24 rounded-md" />
            </div>
            <Skeleton className="h-6 w-20 rounded-lg" />
          </div>

          {/* Account Rows */}
          <div className="space-y-2">
            <div className="p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-700/20 border border-gray-100 dark:border-gray-700 space-y-1.5">
              <div className="flex items-center justify-between">
                <Skeleton className="h-2.5 w-20 rounded" />
                <Skeleton className="h-2.5 w-16 rounded" />
              </div>
              <Skeleton className="h-3.5 w-32 rounded-md" />
            </div>
            <div className="p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-700/20 border border-gray-100 dark:border-gray-700 space-y-1.5">
              <div className="flex items-center justify-between">
                <Skeleton className="h-2.5 w-20 rounded" />
                <Skeleton className="h-2.5 w-16 rounded" />
              </div>
              <div className="flex items-center gap-1.5">
                <Skeleton className="w-5 h-5 rounded-full shrink-0" />
                <Skeleton className="h-3.5 w-32 rounded-md" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
            <Skeleton className="h-3.5 w-24 rounded-md" />
            <div className="flex items-center gap-1.5">
              <Skeleton className="w-8 h-8 rounded-xl" />
              <Skeleton className="w-8 h-8 rounded-xl" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RulesMobileCardSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="p-3.5 sm:p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-sm space-y-3"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <Skeleton className="w-8 h-8 rounded-full shrink-0" />
              <div className="space-y-1.5 min-w-0">
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-3 w-20 rounded-md" />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-6 w-16 rounded-xl" />
              <Skeleton className="w-8 h-8 rounded-xl" />
            </div>
          </div>

          {/* Account Format Preview Box */}
          <div className="p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <Skeleton className="h-2.5 w-24 rounded" />
            <Skeleton className="h-3.5 w-28 rounded-md" />
          </div>

          {/* Keywords Section */}
          <div className="p-2.5 rounded-xl bg-gray-50/50 dark:bg-gray-700/20 border border-gray-100 dark:border-gray-700 space-y-2">
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-2.5 w-8 rounded" />
              <Skeleton className="h-5 w-14 rounded-lg" />
              <Skeleton className="h-5 w-16 rounded-lg" />
            </div>
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-2.5 w-8 rounded" />
              <Skeleton className="h-5 w-14 rounded-lg" />
              <Skeleton className="h-5 w-14 rounded-lg" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Backward compatibility alias
export const MobileCardSkeleton = InboundMobileCardSkeleton;
export const CompactMobileCardSkeleton = InboundMobileCardSkeleton;
